import React, { useEffect, useMemo, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Stack,
  Text,
  PrimaryButton,
  Icon,
  TooltipHost,
  IconButton,
  Shimmer,
  ShimmerElementType,
  MessageBar,
  MessageBarType
} from "@fluentui/react";
import { getNextColor } from "@fluentui/react-charting";
import { getAbbrevatedValue } from "../../../Utils/js-common";
import { CustomMetricsCard } from "../../../SharedComponents/CustomMetricsCard";
import { CustomerHealthHeatMap } from "./CustomerHealthHeatMap";
import { CUSTOMER_HEALTH_STATUS_COLORS, IntuneCustomerHealthTooltips } from "./CustomerHealthView.constants";
import { useCustomerHealthData, HealthViewMode } from "./useCustomerHealthHooks";
import { logEvent } from "../../../helper/AppInsightsHelper";
import { IntuneGaugeSection } from "./IntuneGaugeSection";
import { ExploreDataButtonWithDialog } from "../../../SharedComponents/ExploreDataButtonWithDialog";
import IntuneProductDetails, { IntuneProductDetailsHandle } from "./IntuneProductDetails";

interface ICustomerHealthIntuneContentProps {
  tenantId?: string;
  tenantName?: string;
  viewMode?: HealthViewMode;
  identifierId?: string;
}

export const CustomerHealthIntune: React.FC<ICustomerHealthIntuneContentProps> = ({ tenantId: propTenantId, tenantName, viewMode = "tenant", identifierId }) => {
  const [searchParams] = useSearchParams();

  const tenantId = searchParams.get("id") || propTenantId || "";
  const effectiveId = viewMode === "tpid" ? (identifierId || "") : tenantId;
  const entityLabel = viewMode === "tpid" ? "TPID" : "tenant";

  const intuneDetailsRef = useRef<IntuneProductDetailsHandle>(null);

  const { data: healthResponse, isLoading, isError } = useCustomerHealthData(effectiveId, "Intune", viewMode);

  const healthData = healthResponse?.customerHealthDataList ?? healthResponse;
  const kustoLink = healthResponse?.customerHealthDataKustoLink ?? "";

  const customProps = {
    tenantId: tenantId,
    tenantName: tenantName,
  };

  useEffect(() => {
    if (tenantName) {
      logEvent(
        "Intune Tab - Customer Health",
        "Customer Page",
        "CustomerHealthIntune",
        customProps
      );
    }
  }, [tenantName]);

  const overallData = useMemo(() => {
    if (!healthData) return null;

    const overall = healthData.filter((item: any) => item.productName === "Overall");

    const getValue = (category: string): number | undefined => {
      const entry = overall.find((item: any) => item.category === category);
      if (!entry || entry.value === null || entry.value === undefined || entry.value === "") {
        return undefined;
      }

      const parsedValue = Number(entry.value);
      return Number.isNaN(parsedValue) ? undefined : parsedValue;
    };

    const getMetricValue = (category: string): number => getValue(category) ?? 0;

    const intuneHealthScore = getValue("HealthScore");
    const hasScoreData = intuneHealthScore !== undefined && intuneHealthScore > 0;

    if (!hasScoreData) {
      return {
        hasScoreData: false,
        intuneHealthScore: undefined,
        healthStatus: undefined,
        uat: getMetricValue("UATFeatureRequests"),
        crmFRs: getMetricValue("CRMFeatureRequests"),
        dcr: getMetricValue("DCRFeatureRequests"),
        activeSR: getMetricValue("ActiveSupportRequests"),
        criticalCase: getMetricValue("CritSitCases")
      };
    }

    let healthStatus = "Critical";
    if (intuneHealthScore >= 71) healthStatus = "Healthy";
    else if (intuneHealthScore >= 41) healthStatus = "Warning";

    return {
      hasScoreData: true,
      intuneHealthScore: intuneHealthScore,
      healthStatus,
      uat: getMetricValue("UATFeatureRequests"),
      crmFRs: getMetricValue("CRMFeatureRequests"),
      dcr: getMetricValue("DCRFeatureRequests"),
      activeSR: getMetricValue("ActiveSupportRequests"),
      criticalCase: getMetricValue("CritSitCases")
    };
  }, [healthData]);

  const metricdata = useMemo(
    () => [
      {
        header: "Feature Request",
        description: "Track feature requests across UAT, CRM, and DCR",
        metrics: [
          {
            title: "UAT",
            description: "User Acceptance Testing requests",
            currentValue: (overallData?.uat ?? 0).toString(),
            abbrevatedValue: getAbbrevatedValue(overallData?.uat ?? 0),
            color: getNextColor(0)
          },
          {
            title: "CRM FRs",
            description: "CRM Feature Requests",
            currentValue: (overallData?.crmFRs ?? 0).toString(),
            abbrevatedValue: getAbbrevatedValue(overallData?.crmFRs ?? 0),
            color: getNextColor(1)
          },
          {
            title: "DCRs",
            description: "Design Change Requests",
            currentValue: (overallData?.dcr ?? 0).toString(),
            abbrevatedValue: getAbbrevatedValue(overallData?.dcr ?? 0),
            color: getNextColor(2)
          }
        ]
      },
      {
        header: "Support",
        description: "Track active support cases and critical situations",
        metrics: [
          {
            title: "Active SRs",
            description: "Active Service Requests",
            currentValue: (overallData?.activeSR ?? 0).toString(),
            abbrevatedValue: getAbbrevatedValue(overallData?.activeSR ?? 0),
            color: getNextColor(3)
          },
          {
            title: "Critisit Cases",
            description: "Critical Situation Cases",
            currentValue: (overallData?.criticalCase ?? 0).toString(),
            abbrevatedValue: getAbbrevatedValue(overallData?.criticalCase ?? 0),
            color: getNextColor(4)
          }
        ]
      }
    ],
    [overallData]
  );

  if (isLoading) {
    return (
      <Stack tokens={{ childrenGap: 24 }} styles={{ root: { padding: "16px 24px" } }}>
        {/* AI Summary shimmer */}
        <Shimmer
          shimmerElements={[
            { type: ShimmerElementType.line, width: "100%", height: 48 }
          ]}
          styles={{ root: { borderRadius: 4 } }}
        />

        {/* Score Range shimmer */}
        <Stack horizontal tokens={{ childrenGap: 16 }}>
          {[120, 100, 100].map((w, i) => (
            <Shimmer
              key={i}
              shimmerElements={[
                { type: ShimmerElementType.line, width: w, height: 28 }
              ]}
            />
          ))}
        </Stack>

        {/* Gauge + Metrics + HeatMap shimmer */}
        <Stack horizontal tokens={{ childrenGap: 24 }}>
          {/* Gauge shimmer */}
          <Stack styles={{ root: { flex: "0 0 280px" } }} tokens={{ childrenGap: 8 }}>
            <Shimmer
              shimmerElements={[
                { type: ShimmerElementType.circle, height: 160 },
                { type: ShimmerElementType.gap, width: 120, height: 160 }
              ]}
            />
            <Shimmer shimmerElements={[{ type: ShimmerElementType.line, width: 200, height: 16 }]} />
          </Stack>

          {/* Metrics cards shimmer */}
          <Stack tokens={{ childrenGap: 20 }} styles={{ root: { flex: "0 0 360px" } }}>
            {[0, 1].map((group) => (
              <Stack key={group} tokens={{ childrenGap: 12 }}>
                <Shimmer shimmerElements={[{ type: ShimmerElementType.line, width: 140, height: 16 }]} />
                <Stack horizontal tokens={{ childrenGap: 10 }}>
                  {[0, 1, 2].slice(0, group === 0 ? 3 : 2).map((idx) => (
                    <Shimmer
                      key={idx}
                      shimmerElements={[{ type: ShimmerElementType.line, width: 100, height: 64 }]}
                    />
                  ))}
                </Stack>
              </Stack>
            ))}
          </Stack>

          {/* HeatMap shimmer */}
          <Stack tokens={{ childrenGap: 12 }} styles={{ root: { flex: "1 1 auto" } }}>
            <Shimmer shimmerElements={[{ type: ShimmerElementType.line, width: "60%", height: 20 }]} />
            <Shimmer shimmerElements={[{ type: ShimmerElementType.line, width: "100%", height: 200 }]} />
          </Stack>
        </Stack>

        {/* Product Details shimmer */}
        <Stack tokens={{ childrenGap: 12 }}>
          <Shimmer shimmerElements={[{ type: ShimmerElementType.line, width: 180, height: 20 }]} />
          <Stack horizontal tokens={{ childrenGap: 12 }}>
            {[0, 1, 2, 3].map((i) => (
              <Shimmer
                key={i}
                shimmerElements={[{ type: ShimmerElementType.line, width: 240, height: 100 }]}
              />
            ))}
          </Stack>
        </Stack>
      </Stack>
    );
  }

  if (isError) {
    return (
      <Stack styles={{ root: { padding: "16px 24px" } }}>
        <MessageBar messageBarType={MessageBarType.error}>
          Failed to load customer health data. Please refresh the page or try again later.
        </MessageBar>
      </Stack>
    );
  }

  if (!healthData || healthData.length === 0) {
    return (
      <Stack styles={{ root: { padding: "16px 24px" } }}>
        <MessageBar messageBarType={MessageBarType.warning}>
          No health data available for this {entityLabel}.
        </MessageBar>
      </Stack>
    );
  }

  return (
    <Stack tokens={{ childrenGap: 24 }} styles={{ root: { padding: "16px 24px" } }}>
      <Stack horizontal horizontalAlign="space-between" verticalAlign="center"
        styles={{
          root: {
            padding: "12px 16px",
            backgroundColor: "#F3F2F1",
            borderRadius: 4
          }
        }}>
        <Stack horizontal tokens={{ childrenGap: 8 }} verticalAlign="center">
          <Icon iconName="Lightbulb" styles={{ root: { fontSize: 20, color: "#0078D4" } }} />
          <Text variant="medium" styles={{ root: { fontWeight: 600 } }}>
            AI Summary
          </Text>
          <Text variant="medium" styles={{ root: { color: "#605E5C" } }}>
            To be added soon. Stay tuned.
          </Text>
        </Stack>
        <PrimaryButton text="View More" styles={{ root: { minWidth: 100 } }} />
      </Stack>

      <Stack horizontal tokens={{ childrenGap: 16 }} verticalAlign="center">
        <Text variant="small" styles={{ root: { fontWeight: 600, marginRight: 8 } }}>
          Score Range:
        </Text>
        <div style={{
          border: `2px solid ${CUSTOMER_HEALTH_STATUS_COLORS.Healthy}`,
          borderRadius: 8,
          padding: "4px 10px",
          backgroundColor: "#F0F7E8"
        }}>
          <Text variant="small" styles={{ root: { fontWeight: 500, color: CUSTOMER_HEALTH_STATUS_COLORS.Healthy } }}>
            Healthy 71-100
          </Text>
        </div>
        <div style={{
          border: `2px solid ${CUSTOMER_HEALTH_STATUS_COLORS.Warning}`,
          borderRadius: 8,
          padding: "4px 10px",
          backgroundColor: "#FFFBF0"
        }}>
          <Text variant="small" styles={{ root: { fontWeight: 500, color: CUSTOMER_HEALTH_STATUS_COLORS.Warning } }}>
            Warning 41-70
          </Text>
        </div>
        <div style={{
          border: `2px solid ${CUSTOMER_HEALTH_STATUS_COLORS.Critical}`,
          borderRadius: 8,
          padding: "4px 10px",
          backgroundColor: "#FFF0EB"
        }}>
          <Text variant="small" styles={{ root: { fontWeight: 500, color: CUSTOMER_HEALTH_STATUS_COLORS.Critical } }}>
            Critical 0-40
          </Text>
        </div>
      </Stack>

      <Stack horizontal tokens={{ childrenGap: 24 }}>
        {overallData?.hasScoreData ? (
          <Stack styles={{ root: { flex: "0 0 280px" } }}>
            <IntuneGaugeSection score={overallData?.intuneHealthScore ?? 0} status={overallData?.healthStatus ?? "Critical"} />
          </Stack>
        ) : (
          <Stack styles={{ root: { flex: "0 0 280px", paddingTop: 12 } }}>
            <MessageBar messageBarType={MessageBarType.warning}>No score data available.</MessageBar>
          </Stack>
        )}

        <Stack horizontal tokens={{ childrenGap: 20 }} styles={{ root: { flex: "1 1 auto", minWidth: 0 } }}>
          {overallData?.hasScoreData && (
            <Stack tokens={{ childrenGap: 20 }} styles={{ root: { flex: "0 0 360px" } }}>
              {metricdata.map((group) => (
                <Stack key={group.header} tokens={{ childrenGap: 12 }}>
                  <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 4 }}>
                    <Text variant="medium" styles={{ root: { fontWeight: 600 } }}>
                      {group.header}
                    </Text>
                    <TooltipHost content={group.description}>
                      <IconButton
                        iconProps={{ iconName: "Info" }}
                        styles={{
                          root: { background: "transparent", color: "#605E5C", height: 20, width: 20 },
                          rootHovered: { background: "transparent", color: "#333" }
                        }}
                        ariaLabel={`Info about ${group.header}`}
                      />
                    </TooltipHost>
                  </Stack>
                  <Stack horizontal tokens={{ childrenGap: 10 }} wrap>
                    {group.metrics.map((metric, idx) => (
                      <CustomMetricsCard key={idx} {...metric} />
                    ))}
                  </Stack>
                </Stack>
              ))}
              <ExploreDataButtonWithDialog query={kustoLink} buttonText="Explore Data" style={{ marginTop: 48, marginBottom: 12, backgroundColor: "#0078D4", color: "#FFFFFF", border: "none", borderRadius: 4, padding: "6px 12px" }} />
            </Stack>
          )}

          <Stack tokens={{ childrenGap: 12 }} styles={{ root: { flex: "1 1 auto", width: "100%", minWidth: 0 } }}>
            <Stack horizontal verticalAlign="center" horizontalAlign="space-between" styles={{ root: { maxWidth: 900, width: "100%", paddingLeft: 180 } }}>
              <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 8 }}>
                <Text variant="large" styles={{ root: { fontWeight: 600 } }}>
                  Heat Map by Product and Indicators
                </Text>
                <TooltipHost content={IntuneCustomerHealthTooltips.HeatMap}>
                  <Icon iconName="Info" styles={{ root: { fontSize: 16, color: "#605E5C", cursor: "pointer" } }} />
                </TooltipHost>
              </Stack>
              <Stack horizontal tokens={{ childrenGap: 12 }} verticalAlign="center">
                <Stack horizontal tokens={{ childrenGap: 4 }} verticalAlign="center">
                  <div style={{ width: 12, height: 12, backgroundColor: CUSTOMER_HEALTH_STATUS_COLORS.Healthy, borderRadius: 2 }} />
                  <Text variant="medium" styles={{ root: { fontWeight: 500 } }}>Healthy</Text>
                </Stack>
                <Stack horizontal tokens={{ childrenGap: 4 }} verticalAlign="center">
                  <div style={{ width: 12, height: 12, backgroundColor: CUSTOMER_HEALTH_STATUS_COLORS.Warning, borderRadius: 2 }} />
                  <Text variant="medium" styles={{ root: { fontWeight: 500 } }}>Warning</Text>
                </Stack>
                <Stack horizontal tokens={{ childrenGap: 4 }} verticalAlign="center">
                  <div style={{ width: 12, height: 12, backgroundColor: CUSTOMER_HEALTH_STATUS_COLORS.Critical, borderRadius: 2 }} />
                  <Text variant="medium" styles={{ root: { fontWeight: 500 } }}>Critical</Text>
                </Stack>
                <Stack horizontal tokens={{ childrenGap: 4 }} verticalAlign="center">
                  <div style={{ width: 12, height: 12, backgroundColor: CUSTOMER_HEALTH_STATUS_COLORS.NA, borderRadius: 2 }} />
                  <Text variant="medium" styles={{ root: { fontWeight: 500 } }}>N/A</Text>
                </Stack>
              </Stack>
            </Stack>

            <CustomerHealthHeatMap tenantId={effectiveId} productFamily="Intune" viewMode={viewMode} onCellClick={(product, indicator) => {
              intuneDetailsRef.current?.selectCell(product, indicator);
            }} />
          </Stack>
        </Stack>
      </Stack>

      <IntuneProductDetails ref={intuneDetailsRef} tenantId={effectiveId} viewMode={viewMode} />
    </Stack>
  );
};